import { useState, useEffect, useCallback, useRef } from 'react';
import './SalesAssociate.css';

interface SalesAssociateProps {
  apiBaseUrl: string;
}

const USER_MSG = "I'm not sure about the color...";
const AI_MSG =
  "It's actually our most popular shade this season — and it pairs beautifully with the navy blazer you bought in March";
const TYPE_SPEED = 28;

type Phase =
  | 'idle'
  | 'user-msg'
  | 'typing'
  | 'ai-typing'
  | 'ai-done'
  | 'product'
  | 'btn-visible'
  | 'added'
  | 'fade-out';

export function SalesAssociate(_props: SalesAssociateProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const intervalsRef = useRef<ReturnType<typeof setInterval>[]>([]);

  const [phase, setPhase] = useState<Phase>('idle');
  const [charCount, setCharCount] = useState(0);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const typeText = useCallback(
    (text: string) =>
      new Promise<void>((resolve) => {
        let i = 0;
        setCharCount(0);
        const id = setInterval(() => {
          i++;
          setCharCount(i);
          if (i >= text.length) {
            clearInterval(id);
            resolve();
          }
        }, TYPE_SPEED);
        intervalsRef.current.push(id);
      }),
    [],
  );

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setPhase('idle');
      setCharCount(0);
      await sleep(600);

      if (!loopRef.current) return;
      setPhase('user-msg');
      await sleep(1200);

      if (!loopRef.current) return;
      setPhase('typing');
      await sleep(1400);

      if (!loopRef.current) return;
      setPhase('ai-typing');
      await typeText(AI_MSG);

      if (!loopRef.current) return;
      setPhase('ai-done');
      await sleep(600);

      if (!loopRef.current) return;
      setPhase('product');
      await sleep(800);

      if (!loopRef.current) return;
      setPhase('btn-visible');
      await sleep(1000);

      if (!loopRef.current) return;
      setPhase('added');
      await sleep(3000);

      if (!loopRef.current) return;
      setPhase('fade-out');
      await sleep(900);

      await sleep(400);
    }
  }, [sleep, typeText]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      intervalsRef.current.forEach(clearInterval);
      intervalsRef.current = [];
    };
  }, [runLoop]);

  const showUser = phase !== 'idle';
  const showTyping = phase === 'typing';
  const showAi = [
    'ai-typing',
    'ai-done',
    'product',
    'btn-visible',
    'added',
    'fade-out',
  ].includes(phase);
  const showProduct = ['product', 'btn-visible', 'added', 'fade-out'].includes(
    phase,
  );
  const showBtn = ['btn-visible', 'added', 'fade-out'].includes(phase);
  const isAdded = phase === 'added' || phase === 'fade-out';
  const isFading = phase === 'fade-out';

  const vis = (on: boolean) =>
    isFading ? 'sa-fade-out' : on ? 'sa-visible' : '';

  return (
    <div className="sales-associate">
      <div className="sa-chat">
        {/* User hesitation bubble */}
        <div className={`sa-row sa-row-user ${vis(showUser)}`}>
          <div className="sc-bubble sc-bubble-user">{USER_MSG}</div>
        </div>

        {/* Typing indicator */}
        {showTyping && (
          <div className={`sa-row sa-row-assistant ${vis(true)}`}>
            <div className="sc-bubble sc-bubble-assistant sa-typing-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        {/* AI response with typewriter */}
        {showAi && (
          <div className={`sa-row sa-row-assistant ${vis(true)}`}>
            <div className="sc-bubble sc-bubble-assistant">
              {AI_MSG.slice(0, charCount)}
              {phase === 'ai-typing' && <span className="sa-cursor" />}
            </div>
          </div>
        )}

        {/* Product card */}
        <div className={`sa-product-card sc-glass-card ${vis(showProduct)}`}>
          <div className="sa-product-image" />
          <div className="sa-product-info">
            <span className="sa-product-name">Merino Wool Cardigan</span>
            <span className="sa-product-price">$128</span>
          </div>
        </div>

        {/* Add to Bag button */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            className={`sa-add-btn ${vis(showBtn)} ${isAdded ? 'sa-added' : ''}`}
            type="button"
          >
            {isAdded ? (
              <>
                <span className="sa-check-icon">
                  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="2.5 6 5 8.5 9.5 3.5" />
                  </svg>
                </span>
                Added!
              </>
            ) : (
              'Add to Bag'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
